import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useLocation } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Accordion, AccordionSummary, AccordionDetails, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactMarkdown from 'react-markdown';
import TermsOfService from '../pages/TermsOfService.md';
import { Constants } from '../Constants';
import { UserService } from '../services/UserService';

// Pages the user must always be able to read, even before accepting the ToS.
const LEGAL_ROUTES = ['/terms', '/business-terms', '/privacy'];

const TosConsentGate = () => {
  const { keycloak, initialized } = useKeycloak();
  const location = useLocation();
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Initialize keycloak in UserService if available
  useEffect(() => {
    if (keycloak && initialized) {
      UserService.keycloak = keycloak;
    }
  }, [keycloak, initialized]);

  // Check if user needs to accept ToS
  useEffect(() => {
    const checkTosConsent = async () => {
      if (!keycloak || !initialized || !keycloak.authenticated) {
        return;
      }

      try {
        setLoading(true);
        const response = await UserService.getUserProfile();
        const userData = response?.data;
        setCurrentUser(userData);

        // Show dialog if user hasn't accepted current TOS version
        if (!userData || userData.tosVersion !== Constants.TOS_VERSION) {
          setShowDialog(true);
        }
      } catch (err) {
        // 404 or missing user record - new user, show consent dialog
        if (err.response?.status === 404) {
          setShowDialog(true);
        } else {
          console.error('Error fetching user profile:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    checkTosConsent();
  }, [keycloak, initialized]);

  const handleAgree = async () => {
    try {
      setError(null);
      setLoading(true);

      const response = await UserService.acceptTos(Constants.TOS_VERSION);

      if (response?.status === 200) {
        setShowDialog(false);
        setCurrentUser(response.data);
      } else {
        setError('Failed to save consent. Please try again.');
      }
    } catch (err) {
      console.error('Error accepting ToS:', err);
      setError('Failed to save consent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Don't render if not authenticated or still initializing
  if (!keycloak || !initialized || !keycloak.authenticated) {
    return null;
  }

  // Never block legal pages: the user must be able to read the terms (and other
  // legal documents) before accepting. Only rendering is suppressed here; the
  // consent check above still runs, so leaving the legal page shows the dialog.
  const onLegalPage = LEGAL_ROUTES.includes(location.pathname);
  if (onLegalPage) {
    return null;
  }

  return (
    <Dialog
      open={showDialog}
      disableEscapeKeyDown
      onClose={() => {}} // Prevent closing by clicking outside
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Terms of Service</DialogTitle>
      <DialogContent sx={{ paddingTop: '16px' }}>
        {error && (
          <Alert severity="error" sx={{ marginBottom: '16px' }}>
            {error}
          </Alert>
        )}
        <p>
          To continue using PairCare you need to review and accept our Terms of Service.
        </p>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            View Terms of Service
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ maxHeight: '45vh', overflowY: 'auto' }}>
              <ReactMarkdown>{TermsOfService}</ReactMarkdown>
            </Box>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleAgree}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'I Agree'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TosConsentGate;
